import React, { useEffect, useState } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./PrintPaperSummary.css";

const PrintPaperSummary = () => {
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedAbstracts, setExpandedAbstracts] = useState({});

  useEffect(() => {
    const fetchPapers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URL}/api/v1/submit/get-papers`
        );
        if (response.data?.data) {
          setPapers(response.data.data);
        } else {
          setError("Unexpected data format received from the server.");
        }
      } catch (error) {
        setError("Failed to fetch papers. Please try again later.");
        console.error("Fetch papers error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPapers();
  }, []);

  const toggleAbstract = (paperID) => {
    setExpandedAbstracts(prev => ({
      ...prev,
      [paperID]: !prev[paperID]
    }));
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });
    
    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text("Paper Summary", doc.internal.pageSize.getWidth() / 2, 15, { align: "center" });
    
    // Date
    const today = new Date();
    const formattedDate = today.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Generated on: ${formattedDate}`, 14, 25);
    
    // Prepare table data
    const tableData = papers.map((paper) => {
      const authors = paper.author && Array.isArray(paper.author) ? paper.author : [];
      return [
        paper.paperID,
        paper.title,
        authors.map(author => `${author.firstName} ${author.lastName}`).join(", "),
        authors.map(author => author.email).join(", "),
        paper.keywords || "N/A",
        paper.abstract || "No abstract provided",
        paper.presentationType || "N/A"
      ];
    });
    
    // Create table
    doc.autoTable({
      head: [["ID", "Title", "Authors", "Emails", "Keywords", "Abstract", "Presentation Type"]],
      body: tableData,
      startY: 30,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { 
        fillColor: [52, 58, 64],
        textColor: 255,
        fontStyle: "bold"
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 35 },
        2: { cellWidth: 30 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25 },
        5: { cellWidth: 60, cellHeight: "auto" },
        6: { cellWidth: 25 }
      }
    });
    
    doc.save(`Paper_Summary_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const sortedPapers = [...papers].sort((a, b) => a.paperID - b.paperID);

  return (
    <div className="print-paper-summary-container">
      <div className="print-header-section">
        <h2 className="print-page-title">Paper Summary</h2>
        <p className="print-page-description">
          Comprehensive overview of all submitted papers. Click on abstracts to view full text.
        </p>
      </div>

      <div className="print-controls-section">
        <div className="print-stats-card">
          <span className="print-stat-number">{papers.length}</span>
          <span className="print-stat-label">Papers Submitted</span>
        </div>
        <button 
          className="print-download-button" 
          onClick={handleDownloadPDF}
          disabled={loading || papers.length === 0}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
            <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
          </svg>
          Download PDF Summary
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading papers...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <div className="error-icon">!</div>
          <p className="error-message">{error}</p>
          <button 
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      ) : sortedPapers.length > 0 ? (
        <div className="print-table-container">
          <table className="print-paper-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Authors</th>
                <th>Emails</th>
                <th>Keywords</th>
                <th>Abstract</th>
                <th>Presentation</th>
              </tr>
            </thead>
            <tbody>
              {sortedPapers.map((paper) => {
                const authors = paper.author && Array.isArray(paper.author) ? paper.author : [];
                const isExpanded = expandedAbstracts[paper.paperID];
                const abstract = paper.abstract || "No abstract provided";
                const abstractPreview = abstract.length > 150 && !isExpanded
                  ? `${abstract.substring(0, 150)}...`
                  : abstract;
                
                return (
                  <tr key={paper.paperID}>
                    <td className="print-paper-id">{paper.paperID}</td>
                    <td className="print-paper-title">{paper.title}</td>
                    <td className="print-paper-authors">
                      {authors.length > 0 ? (
                        <div className="print-authors-list">
                          {authors.map((author, index) => (
                            <div key={index} className="print-author-item">
                              <div className="print-author-name">{author.firstName} {author.lastName}</div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        "No authors available"
                      )}
                    </td>
                    <td className="print-paper-emails">
                      {authors.length > 0 ? (
                        <ul>
                          {authors.map((author, index) => (
                            <li key={index}>{author.email}</li>
                          ))}
                        </ul>
                      ) : (
                        "No emails available"
                      )}
                    </td>
                    <td className="print-paper-keywords">
                      {paper.keywords ? (
                        <div className="print-keywords-container">
                          {paper.keywords.split(",").map((keyword, index) => (
                            <span key={index} className="print-keyword-tag">
                              {keyword.trim()}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "N/A"
                      )}
                    </td>
                    <td 
                      className={`print-paper-abstract ${abstract.length > 150 ? 'clickable' : ''} ${isExpanded ? 'expanded' : ''}`}
                      onClick={() => abstract.length > 150 && toggleAbstract(paper.paperID)}
                    >
                      {abstractPreview}
                      {abstract.length > 150 && (
                        <span className="print-abstract-toggle">
                          {isExpanded ? " Show less" : " Show more"}
                        </span>
                      )}
                    </td>
                    <td className="print-paper-presentation">
                      <span className={`print-presentation-tag ${paper.presentationType === 'Oral' ? 'oral' : 'poster'}`}>
                        {paper.presentationType || "N/A"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="print-empty-state">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" fill="currentColor" viewBox="0 0 16 16">
            <path d="M14.5 3a.5.5 0 0 1 .5.5v9a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5v-9a.5.5 0 0 1 .5-.5h13zm-13-1A1.5 1.5 0 0 0 0 3.5v9A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 14.5 2h-13z"/>
            <path d="M3 5.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zM3 8a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9A.5.5 0 0 1 3 8zm0 2.5a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5z"/>
          </svg>
          <h3>No Papers Available</h3>
          <p>There are currently no papers to display. Please check back later.</p>
        </div>
      )}
    </div>
  );
};

export default PrintPaperSummary;